import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Loader2, Pencil, Save, X, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import StatusBadge from './StatusBadge';
import ContactActions from './ContactActions';
import NotesEditor from './NotesEditor';
import SearchFilterBar from './SearchFilterBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const TUTOR_STATUS_OPTIONS = [
  { value: 'new', label: 'New Application' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'interview_scheduled', label: 'Interview Scheduled' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'onboarding_complete', label: 'Onboarding Complete' }
];

/**
 * Tutor Management Panel Component
 * 
 * Manages tutor applications with status updates, notes, and CV access
 */
const TutorManagementPanel = ({ tutors, onRefresh, processing }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [editingDisplayNameId, setEditingDisplayNameId] = useState(null);
  const [displayNameDraft, setDisplayNameDraft] = useState('');
  const [savingDisplayName, setSavingDisplayName] = useState(false);
  const [notifyingId, setNotifyingId] = useState(null);

  const updateStatus = async (tutorId, newStatus, tutorName, tutor) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v4/admin/tutors/${tutorId}/status?status=${newStatus}`,
        { method: 'PATCH', credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Failed to update status');
      toast.success(`${tutorName} status updated to: ${newStatus.replace(/_/g, ' ')}`);
      onRefresh();

      // Optional safe automation: when moving to Approved, offer to send
      // the welcome email (only if the tutor has not already been notified).
      if (newStatus === 'approved' && tutor && !tutor.notified) {
        const sendEmail = window.confirm(
          `Send welcome email to ${tutorName} now?`
        );
        if (sendEmail) {
          await notifyTutor(tutor, { skipConfirm: true });
        }
      } else if (newStatus === 'approved' && tutor?.notified) {
        toast.info('Welcome email was already sent earlier. Use "Notify Tutor" to resend manually.');
      }
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Failed to update status');
    }
  };
  
  const toggleVisibility = async (tutorId, tutorName) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v4/admin/tutors/${tutorId}/toggle`,
        { method: 'PATCH', credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Failed to toggle visibility');
      toast.success(`${tutorName} visibility toggled`);
      onRefresh();
    } catch (error) {
      console.error('Toggle error:', error);
      toast.error('Failed to toggle visibility');
    }
  };
  
  const saveNotes = async (tutorId, notes, tutorName) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v4/admin/tutors/${tutorId}/notes?admin_notes=${encodeURIComponent(notes)}`,
        { method: 'PATCH', credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Failed to save notes');
      toast.success(`Notes saved for ${tutorName}`);
      onRefresh();
    } catch (error) {
      console.error('Notes save error:', error);
      toast.error('Failed to save notes');
    }
  };

  const startEditDisplayName = (tutor) => {
    setEditingDisplayNameId(tutor.id);
    setDisplayNameDraft(tutor.directory_display_name || '');
  };

  const cancelEditDisplayName = () => {
    setEditingDisplayNameId(null);
    setDisplayNameDraft('');
  };

  const saveDisplayName = async (tutor) => {
    const trimmed = (displayNameDraft || '').trim();
    if (trimmed.length > 100) {
      toast.error('Display name must be 100 characters or fewer');
      return;
    }
    try {
      setSavingDisplayName(true);
      const res = await fetch(
        `${BACKEND_URL}/api/v4/admin/tutors/${tutor.id}/display-name`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ directory_display_name: trimmed }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to save display name');
      }
      toast.success(
        trimmed
          ? `Display name updated to "${trimmed}"`
          : 'Display name cleared — directory will show original name'
      );
      cancelEditDisplayName();
      onRefresh();
    } catch (error) {
      console.error('Save display name error:', error);
      toast.error(error.message || 'Failed to save display name');
    } finally {
      setSavingDisplayName(false);
    }
  };

  const notifyTutor = async (tutor, { skipConfirm = false } = {}) => {
    const name = tutor.full_name || 'this tutor';
    if (!skipConfirm) {
      const ok = window.confirm(
        `Send the welcome / acceptance email to ${name} (${tutor.email})?`
      );
      if (!ok) return false;
    }

    try {
      setNotifyingId(tutor.id);
      const res = await fetch(
        `${BACKEND_URL}/api/v4/admin/tutors/${tutor.id}/notify`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      // Parse body defensively — some edge proxies strip bodies on non-2xx
      // responses, so the backend returns 200 with { success: boolean } for
      // both happy and unhappy paths.
      const rawText = await res.text().catch(() => '');
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch (_e) {
        data = {};
      }

      const sendFailed = !res.ok || data?.success === false;

      if (sendFailed) {
        // Backend may return structured failure in either shape:
        //   200 { success:false, error_code, message }
        //   non-200 { detail: { error_code, message } | string }
        const detail = data?.detail;
        const errorCode =
          data?.error_code ||
          (detail && typeof detail === 'object' && detail.error_code) ||
          null;
        const errorMessage =
          data?.message ||
          (detail && typeof detail === 'object' && detail.message) ||
          (typeof detail === 'string' && detail) ||
          'Failed to send welcome email';

        console.error('[NotifyTutor] failed:', {
          status: res.status,
          errorCode,
          errorMessage,
          raw: data,
        });

        // Admin-facing copy per error class
        if (errorCode === 'sender_not_verified') {
          toast.error('Email not sent — sender domain not verified', {
            description:
              'Resend is still in test mode. Verify a domain at resend.com/domains and update SENDER_EMAIL in backend/.env. Until then, only the account owner email can receive messages.',
            duration: 10000,
          });
        } else if (errorCode === 'email_not_configured' || errorCode === 'invalid_api_key') {
          toast.error('Email service not configured', {
            description: errorMessage,
            duration: 10000,
          });
        } else {
          toast.error(`Failed to send welcome email to ${name}`, {
            description: errorMessage,
            duration: 8000,
          });
        }

        return false;
      }

      toast.success(`Welcome email sent to ${name}`);
      onRefresh();
      return true;
    } catch (error) {
      console.error('[NotifyTutor] exception:', error);
      toast.error(`Failed to send welcome email to ${name}`, {
        description: error?.message || 'Unexpected error. Check console/logs.',
        duration: 8000,
      });
      return false;
    } finally {
      setNotifyingId(null);
    }
  };
  
  const downloadCV = (cvUrl, name) => {
    const filename = cvUrl.split('/').pop();
    const apiUrl = `${BACKEND_URL}/api/v4/cv/${filename}`;
    window.open(apiUrl, '_blank');
    toast.success(`Opening CV for ${name}`);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  
  // Get unique subjects for filter
  const allSubjects = [...new Set(tutors.flatMap(t => t.subjects))];
  
  // Filter tutors
  const filteredTutors = tutors.filter(tutor => {
    const matchesSearch = 
      tutor.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tutor.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || tutor.status === statusFilter;
    const matchesSubject = subjectFilter === 'all' || 
      tutor.subjects.some(s => s.toLowerCase().includes(subjectFilter.toLowerCase()));
    return matchesSearch && matchesStatus && matchesSubject;
  });
  
  const SubjectFilter = (
    <Select value={subjectFilter} onValueChange={setSubjectFilter}>
      <SelectTrigger className="w-full md:w-48">
        <SelectValue placeholder="Filter by subject" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Subjects</SelectItem>
        {allSubjects.map(subject => (
          <SelectItem key={subject} value={subject}>{subject}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Tutor Applications ({filteredTutors.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <SearchFilterBar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          statusOptions={TUTOR_STATUS_OPTIONS}
          additionalFilters={SubjectFilter}
          placeholder="Search tutors by name or email..."
        />
        
        <div className="space-y-4">
          {filteredTutors.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No tutors found</p>
          ) : (
            filteredTutors.map(tutor => (
              <Card key={tutor.id} className="border">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {tutor.full_name}
                        </h3>

                        {/* Directory Display Name — admin-editable public-facing name */}
                        <div className="mt-2" data-testid={`v5-display-name-row-${tutor.id}`}>
                          {editingDisplayNameId === tutor.id ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Input
                                value={displayNameDraft}
                                onChange={(e) => setDisplayNameDraft(e.target.value)}
                                placeholder='e.g. "Gardner W."'
                                maxLength={100}
                                className="h-8 max-w-xs"
                                data-testid={`v5-display-name-input-${tutor.id}`}
                                autoFocus
                              />
                              <Button
                                size="sm"
                                className="h-8 bg-[#007A7A] hover:bg-[#006060] text-white gap-1"
                                onClick={() => saveDisplayName(tutor)}
                                disabled={savingDisplayName}
                                data-testid={`v5-save-display-name-${tutor.id}`}
                              >
                                {savingDisplayName ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Save className="h-3.5 w-3.5" />
                                )}
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 gap-1"
                                onClick={cancelEditDisplayName}
                                disabled={savingDisplayName}
                                data-testid={`v5-cancel-display-name-${tutor.id}`}
                              >
                                <X className="h-3.5 w-3.5" />
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-gray-500 uppercase tracking-wide">
                                Directory name:
                              </span>
                              <span
                                className={`text-sm font-medium ${
                                  tutor.directory_display_name
                                    ? 'text-gray-900'
                                    : 'text-gray-400 italic'
                                }`}
                                data-testid={`v5-display-name-value-${tutor.id}`}
                              >
                                {tutor.directory_display_name || 'Not set · uses full name'}
                              </span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 gap-1 text-gray-600 hover:text-gray-900"
                                onClick={() => startEditDisplayName(tutor)}
                                data-testid={`v5-edit-display-name-${tutor.id}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit
                              </Button>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                          <span>{tutor.email}</span>
                          <span>{tutor.phone}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusBadge status={tutor.status} type="tutor" />
                        {tutor.is_public && (
                          <Badge className="bg-green-100 text-green-800">PUBLIC</Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Subjects */}
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Subjects:</p>
                      <div className="flex flex-wrap gap-2">
                        {tutor.subjects.map((subject, idx) => (
                          <Badge key={idx} variant="outline">{subject}</Badge>
                        ))}
                      </div>
                    </div>
                    
                    {/* Timestamps */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Applied:</p>
                        <p className="font-medium">{formatDate(tutor.created_at)}</p>
                      </div>
                      {tutor.approved_at && (
                        <div>
                          <p className="text-gray-500">Approved:</p>
                          <p className="font-medium">{formatDate(tutor.approved_at)}</p>
                        </div>
                      )}
                      {tutor.rejected_at && (
                        <div>
                          <p className="text-gray-500">Rejected:</p>
                          <p className="font-medium">{formatDate(tutor.rejected_at)}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-wrap gap-3 pt-4 border-t">
                      <Select
                        value={tutor.status}
                        onValueChange={(value) => updateStatus(tutor.id, value, tutor.full_name, tutor)}
                        disabled={processing}
                      >
                        <SelectTrigger className="w-48" data-testid={`v5-status-select-${tutor.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TUTOR_STATUS_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadCV(tutor.cv_url, tutor.full_name)}
                        data-testid={`v5-download-cv-${tutor.id}`}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        CV
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleVisibility(tutor.id, tutor.full_name)}
                        disabled={processing}
                        data-testid={`v5-toggle-visibility-${tutor.id}`}
                      >
                        {tutor.is_public ? 'Hide' : 'Show'} in Directory
                      </Button>

                      {/* Notify Tutor — send welcome email. Badge replaces button when already notified. */}
                      {tutor.notified ? (
                        <Badge
                          className="gap-1 bg-green-100 text-green-800 border border-green-200 px-3 py-1.5"
                          data-testid={`v5-notified-badge-${tutor.id}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Tutor Notified
                          {tutor.notified_date && (
                            <span className="ml-1 font-normal">
                              · {formatDate(tutor.notified_date)}
                            </span>
                          )}
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          className="gap-2 bg-[#007A7A] hover:bg-[#006060] text-white"
                          onClick={() => notifyTutor(tutor)}
                          disabled={notifyingId === tutor.id || processing}
                          data-testid={`v5-notify-tutor-${tutor.id}`}
                        >
                          {notifyingId === tutor.id ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Sending…
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4" />
                              Notify Tutor
                            </>
                          )}
                        </Button>
                      )}

                      <ContactActions
                        email={tutor.email}
                        phone={tutor.phone}
                        name={tutor.full_name}
                      />
                    </div>
                    
                    {/* Admin Notes */}
                    <div className="pt-4 border-t">
                      <p className="text-sm font-medium text-gray-700 mb-2">Admin Notes:</p>
                      <NotesEditor
                        initialNotes={tutor.admin_notes}
                        onSave={(notes) => saveNotes(tutor.id, notes, tutor.full_name)}
                        disabled={processing}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TutorManagementPanel;